import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
  UseFormTrigger,
} from 'react-hook-form';
import { useState } from 'react';
import {
  BuildingsModel,
  FeatureModel,
  ResourceCalendar,
  ResourceCalendarDefault,
  ResourceTemplateModel,
} from '../../../../../types';
import { ResourceCalendarFormValues } from '../resource-calendars-form-page';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import ResourceCalendarConfigSkeleton from './form-skeleton';
import { SectionHeader } from '../../../../shared/section-header';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Alert from '@mui/material/Alert';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Grow from '@mui/material/Grow';
import Box from '@mui/material/Box';

interface StepResourceCalendarConfigProps {
  control: Control<ResourceCalendarFormValues>;
  watch: UseFormWatch<ResourceCalendarFormValues>;
  setValue: UseFormSetValue<ResourceCalendarFormValues>;
  trigger: UseFormTrigger<ResourceCalendarFormValues>;
  errors: FieldErrors<ResourceCalendarFormValues>;
  calendarRoles: string[];
  buildingsList: BuildingsModel[];
  features: FeatureModel[];
  templates: ResourceTemplateModel[];
  domainDefault?: ResourceCalendarDefault;
  isEditMode?: boolean;
  selectedCalendar?: ResourceCalendar;
  isLoading?: boolean;
}

interface Option {
  displayValue: string;
  value: string;
}

export default function StepResourceCalendarConfig({
  control,
  watch,
  setValue,
  errors,
  calendarRoles,
  buildingsList,
  features,
  templates,
  isEditMode = false,
  isLoading = false,
}: StepResourceCalendarConfigProps) {
  const [randomizeId, setRandomizeId] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<
    ResourceTemplateModel | null | undefined
  >(null);

  const buildingId = watch('buildingId');
  const selectedBuilding = buildingsList.find(
    (building) => building.buildingId === buildingId
  );
  const buildingName = watch('buildingName');
  const resourceName = watch('resourceName');
  const capacity = watch('capacity');
  const floorName = watch('floorName');
  const floorSection = watch('floorSection');
  const resourceId = watch('resourceId');
  const resourceEmail = watch('resourceEmail');

  const resourceCategoryOptions: Option[] = [
    ...(isAbleToSeeUIElement(
      calendarRoles,
      CalendarRoles.CanSelectCategoryUnknown.value
    )
      ? [{ displayValue: 'Unknown', value: 'CATEGORY_UNKNOWN' }]
      : []),
    ...(isAbleToSeeUIElement(
      calendarRoles,
      CalendarRoles.CanSelectConferenceRoom.value
    )
      ? [{ displayValue: 'Conference Room', value: 'CONFERENCE_ROOM' }]
      : []),
    ...(isAbleToSeeUIElement(calendarRoles, CalendarRoles.CanSelectOther.value)
      ? [{ displayValue: 'Other', value: 'OTHER' }]
      : []),
  ];

  // Email is generated by Google's API in the backend

  const hasCapacity = capacity !== 0 && capacity !== null;
  const hasBuildingName =
    buildingName !== '' && buildingName !== null && buildingName !== undefined;
  const hasFloorName = floorName !== '';
  const hasFloorSection = floorSection !== '';
  const hasResourceName = resourceName !== '';

  const generatedResourceName = `${
    hasBuildingName ? buildingName : buildingId
  }${hasFloorName ? `-${floorName}` : ''}${
    hasFloorSection ? `-${floorSection}` : ''
  }${hasFloorName || hasFloorSection ? '-' : ''}${
    hasResourceName ? resourceName : ''
  } ${hasCapacity ? `(${capacity})` : ''}`;

  const handleTemplateChange = (templateId: string) => {
    const template = templates.find(
      (template) => template.id === parseInt(templateId)
    );
    if (template) {
      setValue('resourceName', template.name, { shouldDirty: true });
      setValue('capacity', template.capacity ? template.capacity : 0, {
        shouldDirty: true,
      });
      if (template.resourceCategory !== 'Resource Category') {
        setValue('resourceCategory', template.resourceCategory, {
          shouldDirty: true,
        });
      }
      setValue('resourceType', template.resourceType, { shouldDirty: true });
      setValue('buildingId', template.buildingId, { shouldDirty: true });
      setValue('floorName', template.floorName, { shouldDirty: true });
      setValue('floorSection', template.floorSection, { shouldDirty: true });
      const building = buildingsList.find(
        (building) => building.buildingId === template.buildingId
      );
      if (building) {
        setValue('buildingName', building.buildingName, { shouldDirty: true });
      }
    }
    setSelectedTemplate(template);
  };

  const handleBuildingChange = (buildingId: string) => {
    const building = buildingsList?.find(
      (building) => building.buildingId === buildingId
    );

    if (building) {
      setValue('buildingName', building.buildingName, { shouldDirty: true });

      // Automatically select the first floor if available
      if (building.floorNames && building.floorNames.length > 0) {
        setValue('floorName', building.floorNames[0], { shouldDirty: true });
      } else {
        setValue('floorName', '', { shouldDirty: true });
      }
    } else {
      setValue('floorName', '', { shouldDirty: true });
    }
  };

  const handleRandomizeIdChange = (checked: boolean) => {
    if (checked) {
      const nowDate = new Date();
      nowDate.setTime(
        nowDate.getTime() + nowDate.getTimezoneOffset() * 60 * 1000
      );
      const now = Math.round(nowDate.getTime()).toString();
      setValue('resourceId', now, { shouldDirty: true });
    } else {
      setValue('resourceId', resourceName, { shouldDirty: true });
    }
    setRandomizeId(checked);
  };

  const isFieldDisabled = (fieldRole: string) => {
    return !isAbleToSeeUIElement(calendarRoles, fieldRole);
  };

  const disableDropDownOptions = (option: Option) => {
    const roleMap = {
      CATEGORY_UNKNOWN: CalendarRoles.CanSelectCategoryUnknown.value,
      CONFERENCE_ROOM: CalendarRoles.CanSelectConferenceRoom.value,
      OTHER: CalendarRoles.CanSelectOther.value,
    };
    return !isAbleToSeeUIElement(
      calendarRoles,
      roleMap[option.value as keyof typeof roleMap]
    );
  };

  if (isLoading) {
    return <ResourceCalendarConfigSkeleton isEditMode={isEditMode} />;
  }

  return (
    <Box
      sx={{
        viewTransitionName: 'steps',
      }}
      gap={2}
      width={'100%'}
    >
      <SectionHeader sx={{ mb: 2 }}>
        Resource Calendar Configuration
      </SectionHeader>
      <Stack flexDirection="row" gap={2} pb={3}>
        <TextField
          inputProps={{
            id: 'generatedResourceName',
          }}
          InputLabelProps={{
            htmlFor: 'generatedResourceName',
          }}
          label="Auto-Generated Resource Name"
          sx={{ flex: 1, minWidth: 300 }}
          disabled
          value={generatedResourceName}
        />
        <Box sx={{ flex: 1, minWidth: 300 }} />
      </Stack>

      {!isEditMode && (
        <Stack flexDirection="row" gap={2} pb={3}>
          <TextField
            inputProps={{
              id: 'template',
            }}
            InputLabelProps={{
              htmlFor: 'template',
            }}
            select
            label="Template"
            sx={{ flex: 1, minWidth: 300 }}
            value={selectedTemplate?.id || ''}
            onChange={(e) => handleTemplateChange(e.target.value)}
          >
            {templates?.length > 0 ? (
              templates.map((template) => (
                <MenuItem value={template.id} key={template.id}>
                  {template.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem value={''} disabled>
                {templates ? 'No Templates Available' : 'Loading Templates...'}
              </MenuItem>
            )}
          </TextField>
          <Box
            sx={{
              flex: 1,
              minWidth: 300,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={randomizeId}
                  onChange={(e) => handleRandomizeIdChange(e.target.checked)}
                />
              }
              label="Randomize Id"
            />
          </Box>
        </Stack>
      )}

      {isEditMode && (
        <Stack flexDirection="row" gap={2} pb={3}>
          <TextField
            label="Public Email"
            sx={{ flex: 1, minWidth: 300 }}
            disabled
            value={resourceEmail || ''}
          />
          <TextField
            label="Resource ID"
            sx={{ flex: 1, minWidth: 300 }}
            disabled
            value={resourceId || ''}
          />
        </Stack>
      )}

      <Stack flexDirection={'row'} gap={2} pb={3}>
        <Controller
          name="resourceCategory"
          control={control}
          render={({ field }) => (
            <TextField
              inputProps={{
                id: 'resourceCategory',
              }}
              InputLabelProps={{
                htmlFor: 'resourceCategory',
              }}
              required
              select
              label="Resource Category"
              sx={{ flex: 1, minWidth: 300 }}
              value={field.value || ''}
              onChange={field.onChange}
              error={!!errors.resourceCategory}
              helperText={errors?.resourceCategory?.message}
              disabled={
                isEditMode &&
                isFieldDisabled(
                  CalendarRoles.CanEditResourceCalendarCategory.value
                )
              }
            >
              {resourceCategoryOptions?.map((option) => (
                <MenuItem
                  disabled={isEditMode ? disableDropDownOptions(option) : false}
                  value={option.value}
                  key={option.value}
                >
                  {option.displayValue}
                </MenuItem>
              )) || []}
            </TextField>
          )}
        />
        <Controller
          name="resourceType"
          control={control}
          render={({ field }) => (
            <TextField
              inputProps={{
                id: 'resourceType',
              }}
              InputLabelProps={{
                htmlFor: 'resourceType',
              }}
              label="Resource Type"
              sx={{ flex: 1, minWidth: 300 }}
              value={field.value || ''}
              onChange={field.onChange}
              disabled={
                isEditMode &&
                isFieldDisabled(CalendarRoles.CanEditResourceCalendarType.value)
              }
            />
          )}
        />
      </Stack>

      <Stack flexDirection={'row'} gap={2} pb={3}>
        <Controller
          name="buildingId"
          control={control}
          render={({ field }) => (
            <TextField
              required
              inputProps={{
                id: 'buildingId',
              }}
              InputLabelProps={{
                htmlFor: 'buildingId',
              }}
              label="Building"
              error={!!errors.buildingId}
              helperText={errors?.buildingId?.message}
              value={field.value || ''}
              onChange={(e) => {
                field.onChange(e.target.value);
                handleBuildingChange(e.target.value);
              }}
              select
              sx={{ flex: 1, minWidth: 300 }}
              autoComplete="off"
              disabled={
                isEditMode &&
                isFieldDisabled(
                  CalendarRoles.CanEditResourceCalendarBuilding.value
                )
              }
            >
              <MenuItem value="" disabled>
                Select a building
              </MenuItem>
              {(buildingsList || []).map((building) => (
                <MenuItem value={building.buildingId} key={building.buildingId}>
                  {building.buildingName}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <Stack flexDirection={'row'} gap={2} sx={{ flex: 1, minWidth: 300 }}>
          <Controller
            name="floorName"
            control={control}
            render={({ field }) => (
              <TextField
                inputProps={{
                  id: 'floorName',
                }}
                InputLabelProps={{
                  htmlFor: 'floorName',
                }}
                disabled={
                  buildingId === '' ||
                  (isEditMode &&
                    isFieldDisabled(
                      CalendarRoles.CanEditResourceCalendarFloor.value
                    ))
                }
                select
                required
                label="Floor"
                sx={{ flex: 1, minWidth: 200 }}
                value={field.value || ''}
                onChange={field.onChange}
                error={!!errors.floorName}
                helperText={errors?.floorName?.message}
              >
                <MenuItem value="" disabled>
                  Select a floor
                </MenuItem>
                {(selectedBuilding?.floorNames || []).map((floor) => (
                  <MenuItem value={floor} key={floor}>
                    {floor}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="floorSection"
            control={control}
            render={({ field }) => (
              <TextField
                inputProps={{
                  id: 'floorSection',
                }}
                InputLabelProps={{
                  htmlFor: 'floorSection',
                }}
                label="Floor Section"
                sx={{ flex: 1, minWidth: 200 }}
                value={field.value || ''}
                onChange={field.onChange}
                disabled={
                  isEditMode &&
                  isFieldDisabled(
                    CalendarRoles.CanEditResourceCalendarFloorSection.value
                  )
                }
              />
            )}
          />
        </Stack>
      </Stack>

      <Stack flexDirection={'row'} gap={2} pb={3}>
        <Controller
          name="resourceName"
          control={control}
          render={({ field }) => (
            <TextField
              inputProps={{
                id: 'resourceName',
              }}
              required
              InputLabelProps={{
                htmlFor: 'resourceName',
              }}
              label="Resource Name"
              sx={{ flex: 1, minWidth: 300 }}
              value={field.value || ''}
              onChange={field.onChange}
              error={!!errors.resourceName}
              helperText={errors?.resourceName?.message}
              disabled={
                isEditMode &&
                isFieldDisabled(CalendarRoles.CanEditResourceCalendarName.value)
              }
            />
          )}
        />
        <Controller
          name="capacity"
          control={control}
          render={({ field }) => (
            <TextField
              inputProps={{
                id: 'capacity',
                min: 1,
              }}
              InputLabelProps={{
                htmlFor: 'capacity',
              }}
              required
              label="Capacity"
              type="number"
              sx={{ flex: 1, minWidth: 200 }}
              value={field.value ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  field.onChange('');
                } else {
                  const numValue = Number(value);
                  field.onChange(isNaN(numValue) ? '' : numValue);
                }
              }}
              error={!!errors.capacity}
              helperText={errors?.capacity?.message}
              disabled={
                isEditMode &&
                isFieldDisabled(
                  CalendarRoles.CanEditResourceCalendarCapacity.value
                )
              }
            />
          )}
        />
      </Stack>

      <Stack flexDirection="row" gap={2} pb={3}>
        <Controller
          name="featureInstances"
          control={control}
          render={({ field }) => {
            const fieldValue = Array.isArray(field.value) ? field.value : [];
            return (
              <Autocomplete
                multiple
                autoComplete
                autoHighlight
                id="featureInstances"
                options={features || []}
                sx={{ flex: 1, minWidth: 300 }}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) =>
                  option.name === value?.name
                }
                value={fieldValue}
                onChange={(_event, value) => {
                  field.onChange(value);
                }}
                disabled={
                  isEditMode &&
                  isFieldDisabled(
                    CalendarRoles.CanEditResourceCalendarFeatures.value
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Features"
                    placeholder="Type and press Enter"
                  />
                )}
              />
            );
          }}
        />
        <Box sx={{ flex: 1, minWidth: 300 }} />
      </Stack>

      <Stack flexDirection="row" gap={2} pb={3}>
        <Controller
          name="userVisibleDescription"
          control={control}
          render={({ field }) => (
            <TextField
              inputProps={{
                id: 'userVisibleDescription',
              }}
              InputLabelProps={{
                htmlFor: 'userVisibleDescription',
              }}
              label="User Visible Description"
              multiline
              rows={2}
              value={field.value || ''}
              onChange={field.onChange}
              sx={{ flex: 1, minWidth: 300 }}
              autoComplete="off"
              disabled={
                isEditMode &&
                isFieldDisabled(
                  CalendarRoles.CanEditResourceCalendarUserVisibleDescription
                    .value
                )
              }
            />
          )}
        />
        <Controller
          name="resourceDescription"
          control={control}
          render={({ field }) => (
            <TextField
              inputProps={{
                id: 'description',
              }}
              InputLabelProps={{
                htmlFor: 'description',
              }}
              label="Internal Description"
              multiline
              rows={2}
              value={field.value || ''}
              onChange={field.onChange}
              sx={{ flex: 1, minWidth: 300 }}
              autoComplete="off"
              disabled={
                isEditMode &&
                isFieldDisabled(
                  CalendarRoles.CanEditResourceCalendarInternalDescription.value
                )
              }
            />
          )}
        />
      </Stack>

      <Grow in={true}>
        <Alert severity="info">
          Setting resource information, such as building, floor, or capacity,
          affects the auto-generated resource name and room search. Template's
          sharing settings may take several minutes to process.
        </Alert>
      </Grow>
    </Box>
  );
}
